<template lang="html">
  <md-table
    v-model="usersList.mdData"
    :md-sort.sync="currentSort"
    :md-sort-order.sync="currentSortOrder"
    :md-sort-fn="customUsersSort"
    md-card
    md-fixed-header
    @md-selected="selectUser"
  >
    <md-table-toolbar>
      <div class="md-toolbar-section-start">
        <h5>
          <b> {{ $t("user.generalUserList") }}</b>
        </h5>
        <input
          v-if="!loading"
          @input="searchUser"
          v-model="filter_name"
          class="form-control col-6 ml-2"
          :placeholder="$t('users.searchByNameUsernameEmail')"
        />
        <p v-if="loading" class="loading-container">
          <b-spinner variant="primary" label="Spinning" />
          {{ $t("general.searching") }}
        </p>
      </div>

      <div v-if="scCanCreate" md-clearable class="md-toolbar-section-end">
        <router-link to="/new-user" class="btn btn-primary new-user">
          {{ $t("user.newUser") }}
        </router-link>
      </div>
      <md-table-pagination
        v-if="
          $store.state.user.user_type.type_id === consts.UserType.ADMIN ||
          $store.state.user.user_type.type_id === consts.UserType.OPERATIONS
        "
        :md-page-size="rowsPerPage"
        :md-page-options="[10, 25, 50, 100]"
        :md-update="updatePagination"
        :md-data.sync="usersList"
      />
    </md-table-toolbar>

    <md-table-empty-state :md-description="loading ? `${$t('general.searching')}` : `${$t('general.noDataToShow')}`">
    </md-table-empty-state>

    <md-table-row
      slot="md-table-row"
      slot-scope="{ item }"
      md-selectable="single"
      :class="{ selected: item.user_id == selectedUser.user_id }"
    >
      <md-table-cell>
        <div>
          <img :src="item.photo_url" class="user-photo" v-if="item.photo_url" />
          <img src="@/assets/default-user.jpg" class="user-photo" v-else />
        </div>
      </md-table-cell>
      <md-table-cell :md-label="$t('user.firstName')" md-sort-by="name">{{ item.name }}</md-table-cell>
      <md-table-cell :md-label="$t('user.lastName')" md-sort-by="last_name">{{ item.last_name }}</md-table-cell>
      <md-table-cell :md-label="$t('user.username')" md-sort-by="username">{{ item.username }}</md-table-cell>
      <md-table-cell :md-label="$t('user.role')" md-sort-by="role">
        {{ item.user_type.name }}
      </md-table-cell>
      <md-table-cell :md-label="$t('user.email')" md-sort-by="email">{{ item.email }}</md-table-cell>
      <md-table-cell :md-label="$t('user.phone')" md-sort-by="phone">{{ item.phone }}</md-table-cell>
      <md-table-cell v-if="scCanUpdate || scCanDelete" :md-label="$t('general.actions')">
        <b-icon
          v-if="scCanUpdate"
          @click="editUser(item)"
          icon="pencil-square"
          aria-hidden="true"
          class="mr-3 action-icon"
        ></b-icon>
        <b-icon
          v-if="scCanDelete && item.user_id !== userData.user_id"
          @click="deleteUser(item)"
          icon="trash-fill"
          aria-hidden="true"
          class="action-icon"
        ></b-icon>
      </md-table-cell>
    </md-table-row>
  </md-table>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import { getAllUsersV2, searchAllUsersV2 } from "@/api";

export default {
  name: "UsersTable",
  props: {
    users: {
      type: Array,
      required: true,
    },
    selectUser: {
      type: Function,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedUser: {
      type: Object,
      required: false,
    },
    scCanCreate: {
      type: Boolean,
      required: true,
    },
    scCanUpdate: {
      type: Boolean,
      required: true,
    },
    fnDeleteUser: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      currentSort: "names",
      currentSortOrder: "desc",
      scCanDelete: false,
      filter_name: null,
      usersFiltered: [],
      rowsPerPage: 25,
      usersList: {
        mdCount: 25,
        mdPage: 1,
        mdData: [],
      },
    };
  },
  watch: {
    users: function (newUsers, oldUsers) {
      this.usersList.mdData = newUsers;
    },
  },
  computed: {
    ...mapState("Users", ["loadingUsers"]),
    ...mapState({
      userData: (state) => state.user,
    }),
  },
  methods: {
    ...mapActions("Users", ["apiDeleteUser"]),
    ...mapMutations('Users', ['setUserSelected', 'setUsers']),
    getPolicy() {
      const componentName = "Users";
      let menuPolicy = null;
      this.$store.state.menuPolicy.forEach((mp) => {
        if (menuPolicy == null) {
          if (mp.vue_component === componentName) {
            menuPolicy = mp;
          } else if (mp.sub_menu) {
            menuPolicy = mp.sub_menu.find((smp) => smp.vue_component === componentName);
          }
        }
      });
      if (menuPolicy) {
        this.scCanDelete = menuPolicy.policy.can_delete;
      }
    },
    editUser(user) {
      if (user.user_id == this.userData.user_id) {
        this.$router.push({ path: "settings", query: { tab: "Profile" } });
      } else {
        this.$router.push({ name: "UpdateUser", params: { id: user.user_id } });
      }
    },
    deleteUser(user) {
      this.$bvModal
        .msgBoxConfirm(this.$t("user.deleteUserConfirmation"), {
          size: "sm",
          buttonSize: "sm",
          cancelVariant: "light",
          okVariant: "danger",
          okTitle: this.$t("general.accept"),
          cancelTitle: this.$t("general.cancel"),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then(async (value) => {
          if (value) {
            await this.apiDeleteUser();
            this.fnDeleteUser();
            if (process.env.VUE_APP_SENTRY_ENV == "production") {
              this.$analytics.logEvent("web_users_delete");
            }
          }
        });
    },

    customUsersSort(value) {
      return value.sort((a, b) => {
        const sortBy = this.currentSort;
        if (sortBy != "role" && !a[sortBy] && !b[sortBy]) {
          return 0;
        } else if (sortBy != "role" && a[sortBy] && !b[sortBy]) {
          return -1;
        } else if (sortBy != "role" && !a[sortBy] && b[sortBy]) {
          return 1;
        } else {
          switch (sortBy) {
            case "role":
              if (this.currentSortOrder === "desc") {
                return a["user_type"].name.localeCompare(b["user_type"].name);
              }
              return b["user_type"].name.localeCompare(a["user_type"].name);
              break;

            default:
              if (this.currentSortOrder === "desc") {
                return a[sortBy].localeCompare(b[sortBy]);
              }
              return b[sortBy].localeCompare(a[sortBy]);
              break;
          }
        }
      });
    },

    searchUser() {
      if (this.$store.state.user.user_type.type_id === this.consts.UserType.ADMIN ||
      this.$store.state.user.user_type.type_id === this.consts.UserType.OPERATIONS) {
        if (this.filter_name) {
          const filter = `&name=${this.filter_name}`;
          searchAllUsersV2(1, this.rowsPerPage, filter).then((response) => {
            this.rowsPerPage = response.count;
            this.usersList = {
              mdCount: response.total_rows,
              mdPage: response.current_page,
              mdData: response.users,
            };
          });
        } else {
          this.updatePagination(1, 25);
        }
      } else {
        this.usersFiltered = [];
        if (this.filter_name) {
          let filter_val = this.filter_name.toLowerCase();
          this.usersList.mdData = this.users.filter(
            (user) =>
              (user.username && user.username.toLowerCase().includes(filter_val)) ||
              (user.email && user.email.toLowerCase().includes(filter_val)) ||
              (user.name && user.name.toLowerCase().includes(filter_val)) ||
              (user.last_name && user.last_name.toLowerCase().includes(filter_val))
          );
        } else {
          this.usersList.mdData = this.users;
        }
      }
    },

    updatePagination(page, pageSize, sort, sortOrder) {
      // console.log("pagination has updated", page, pageSize, sort, sortOrder);
      const filters = '';
      if (this.filter_name) {
        const filter = `&name=${this.filter_name}`;
        searchAllUsersV2(page, pageSize, filter).then((response) => {
          this.rowsPerPage = response.count;
          this.setUserSelected(response.users[0]);
          this.usersList = {
            mdCount: response.total_rows,
            mdPage: response.current_page,
            mdData: response.users,
          };
        });
      } else {
        getAllUsersV2(page, pageSize, filters).then((response) => {
          this.rowsPerPage = pageSize;
          this.setUserSelected(response.users[0]);
          this.usersList = {
            mdCount: response.total_rows,
            mdPage: response.current_page,
            mdData: response.users,
          };
        });
      }
    },
  },

  mounted() {
    this.usersFiltered = this.users;
    if (
      this.$store.state.user.user_type.type_id === this.consts.UserType.ADMIN ||
      this.$store.state.user.user_type.type_id === this.consts.UserType.OPERATIONS
    ) {
      this.updatePagination(1, 25);
    }
    this.getPolicy();
  },
};
</script>

<style lang="scss" scoped>
.new-user {
  color: white !important;

  &:hover {
    text-decoration: none !important;
  }
}

svg.action-icon {
  font-size: 16px;
  color: gray;
  cursor: pointer;
}
</style>
